import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import {
  AuthSessionResponseSchema,
  MeResponseSchema,
  LogoutResponseSchema,
  type AuthUser,
  type MeResponse,
  type OnboardingRequest,
  type OrgSummary,
} from "@pakker/shared";

import { apiRequest } from "../../api/client.js";
import { AuthContext, type AuthState } from "./auth-context.js";

export function AuthProvider({ children }: { readonly children: ReactNode }) {
  const [status, setStatus] = useState<AuthState["status"]>("loading");
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<OrgSummary | null>(null);

  const applyAuthenticatedResponse = useCallback((response: MeResponse) => {
    setUser(response.user);
    setOrganization(response.orgs[0] ?? null);
    setStatus("authenticated");
  }, []);

  const refresh = useCallback(async () => {
    try {
      const response = await apiRequest("/api/me", { schema: MeResponseSchema });
      applyAuthenticatedResponse(response);
    } catch {
      setUser(null);
      setOrganization(null);
      setStatus("anonymous");
    }
  }, [applyAuthenticatedResponse]);

  useEffect(() => {
    // Discard cached identities from the retired temporary-password flow.
    sessionStorage.removeItem("pakker.restricted-user");
    void refresh();
  }, [refresh]);

  const requestMagicLink = useCallback(async (email: string) => {
    await apiRequest("/api/auth/magic-link", {
      schema: LogoutResponseSchema,
      method: "POST",
      body: { email },
    });
  }, []);

  const verifyMagicLink = useCallback(
    async (token: string) => {
      const response = await apiRequest("/api/auth/magic-link/verify", {
        schema: AuthSessionResponseSchema,
        method: "POST",
        body: { token },
      });
      await refresh();
      return response.user;
    },
    [refresh],
  );

  const completeOnboarding = useCallback(
    async (input: OnboardingRequest) => {
      await apiRequest("/api/auth/onboarding", {
        schema: LogoutResponseSchema,
        method: "POST",
        body: input,
      });
      await refresh();
    },
    [refresh],
  );

  const setUnitSystemOverride = useCallback(
    async (value: "metric" | "imperial" | null) => {
      await apiRequest("/api/me", {
        schema: MeResponseSchema,
        method: "PATCH",
        body: { unitSystemOverride: value },
      });
      await refresh();
    },
    [refresh],
  );

  const endLocalSession = useCallback(() => {
    setUser(null);
    setOrganization(null);
    setStatus("anonymous");
  }, []);

  const logout = useCallback(async () => {
    await apiRequest("/api/auth/logout", {
      schema: LogoutResponseSchema,
      method: "POST",
    });
    endLocalSession();
  }, [endLocalSession]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      organization,
      refresh,
      requestMagicLink,
      verifyMagicLink,
      completeOnboarding,
      setUnitSystemOverride,
      endLocalSession,
      logout,
    }),
    [
      status,
      user,
      organization,
      refresh,
      requestMagicLink,
      verifyMagicLink,
      completeOnboarding,
      setUnitSystemOverride,
      endLocalSession,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
